import { useCallback, useEffect, useRef } from "react";
import { SESSION_KEEPALIVE_ENABLED } from "../config/sessionKeepAlive";
import { fetchSessionKeepAlive } from "../services/sessionApi";
import { extendSessionFromServer, isAuthenticated } from "../utils/auth";

/** Global heartbeat interval while the tab is active (1 minute). */
export const HEARTBEAT_INTERVAL_MS = 60 * 1000;

/**
 * Low-level heartbeat runner — single interval per provider instance.
 * Restarts when the document becomes visible again (tab throttling recovery).
 */
export function useSessionHeartbeat({
  enabled,
  onKeepAliveSuccess,
  onKeepAliveFailure,
  onSessionExpired,
}) {
  const intervalRef = useRef(null);
  const inFlightRef = useRef(false);
  const enabledRef = useRef(enabled);

  enabledRef.current = enabled;

  const clearHeartbeat = useCallback(() => {
    if (intervalRef.current != null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const runKeepAlive = useCallback(async () => {
    if (!SESSION_KEEPALIVE_ENABLED) return;
    if (!enabledRef.current || inFlightRef.current) return;
    if (!isAuthenticated()) return;

    inFlightRef.current = true;
    try {
      const payload = await fetchSessionKeepAlive();
      const sessionExpiresIn =
        payload?.sessionExpiresIn ?? payload?.SessionExpiresIn;
      const ok =
        payload?.success === true ||
        payload?.Success === true ||
        typeof sessionExpiresIn === "number";
      if (ok && typeof sessionExpiresIn === "number") {
        extendSessionFromServer(sessionExpiresIn);
        onKeepAliveSuccess?.({ ...payload, sessionExpiresIn });
      } else {
        onKeepAliveFailure?.(new Error("Invalid keep-alive response"));
      }
    } catch (err) {
      if (err?.response?.status === 401 || err?.isUnauthorized) {
        onSessionExpired?.(err);
        return;
      }
      onKeepAliveFailure?.(err);
    } finally {
      inFlightRef.current = false;
    }
  }, [onKeepAliveFailure, onKeepAliveSuccess, onSessionExpired]);

  const startHeartbeat = useCallback(() => {
    clearHeartbeat();
    if (!enabledRef.current) return;

    runKeepAlive();
    intervalRef.current = window.setInterval(runKeepAlive, HEARTBEAT_INTERVAL_MS);
  }, [clearHeartbeat, runKeepAlive]);

  useEffect(() => {
    if (!SESSION_KEEPALIVE_ENABLED || !enabled) {
      clearHeartbeat();
      return undefined;
    }

    startHeartbeat();

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        startHeartbeat();
      }
    };

    const onOnline = () => {
      if (enabledRef.current) startHeartbeat();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);

    return () => {
      clearHeartbeat();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
  }, [enabled, startHeartbeat, clearHeartbeat]);

  return { runKeepAlive, startHeartbeat, clearHeartbeat };
}
